import React from 'react';
import useAmplifyContext from '../../../Contexts/AmplifyContext';

// The server rejects an expired token with 401, but a browser cannot see that: a failed
// WebSocket handshake surfaces as a bare `error` event plus close code 1006, with no
// status. So the client cannot learn "your token expired" from the connection - it has
// to decide before connecting, from the expiry it already holds.
//
// Without this, react-use-websocket reconnects forever with whatever `idToken` currently
// is. An expired token therefore produces an endless reconnect loop that never recovers,
// which is what production showed: the same `expiredAt` re-presented for 8+ hours.
//
// Treat a token as unusable slightly before it actually expires, so we do not race the
// handshake with expiry.
export const EXPIRY_SKEW_SECONDS = 30;

// The socket reports an error on every failed attempt, and its first ten retries are
// 500ms apart, so an un-throttled refresh-on-error would storm Cognito. One refresh per
// window is enough: the Amplify hook owns the retry/backoff once it starts.
export const REFRESH_THROTTLE_MS = 15000;

export function isExpired(expiresAt: number | undefined, nowSeconds: number): boolean {
  if (expiresAt === undefined) {
    return false;
  }
  return expiresAt - EXPIRY_SKEW_SECONDS <= nowSeconds;
}

interface UsableIdToken {
  // The token to hand to the server, or undefined when it must not be used.
  idToken: string | undefined;
  // False while we are waiting for a refresh; callers should not attempt to connect.
  isUsable: boolean;
  // Ask for a fresh token. Safe to call repeatedly - the Amplify refresh hook owns its
  // own retry/backoff schedule.
  requestRefresh: () => void;
}

export default function useUsableIdToken(): UsableIdToken {
  const { idToken, idTokenExpiresAt, refreshToken } = useAmplifyContext();

  const [expired, setExpired] = React.useState(
    () => isExpired(idTokenExpiresAt, Math.floor(Date.now() / 1000)),
  );

  // Re-evaluate on a timer as well as on token change: a tab can be suspended past the
  // expiry without any state changing, and that is exactly the case that used to wedge
  // the connection.
  React.useEffect(() => {
    const evaluate = () => setExpired(isExpired(idTokenExpiresAt, Math.floor(Date.now() / 1000)));

    evaluate();
    const interval = window.setInterval(evaluate, 10000);
    return () => window.clearInterval(interval);
  }, [idTokenExpiresAt]);

  // A suspended tab resumes with a stale token and no state change to trigger a
  // re-evaluation, so check on wake and when connectivity returns.
  React.useEffect(() => {
    const evaluate = () => setExpired(isExpired(idTokenExpiresAt, Math.floor(Date.now() / 1000)));

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        evaluate();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', evaluate);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', evaluate);
    };
  }, [idTokenExpiresAt]);

  const lastRefreshRequestRef = React.useRef(0);
  const requestRefresh = React.useCallback(() => {
    const now = Date.now();
    if (now - lastRefreshRequestRef.current < REFRESH_THROTTLE_MS) {
      return;
    }
    lastRefreshRequestRef.current = now;
    refreshToken();
  }, [refreshToken]);

  // Once expired, ask for a refresh rather than waiting for the scheduled one: the
  // schedule may be mid-backoff, or its timer may have been frozen with the tab.
  React.useEffect(() => {
    if (expired) {
      requestRefresh();
    }
  }, [expired, requestRefresh]);

  return React.useMemo(() => ({
    idToken: expired ? undefined : idToken,
    isUsable: !!idToken && !expired,
    requestRefresh,
  }), [expired, idToken, requestRefresh]);
}
